import { Entry } from '@/shared/types'
import { useState } from 'react'
import styled from 'styled-components'
import { Button, Image } from '../../atoms'
import { FormattedMessage } from 'react-intl'
import { strings } from './FeedItem.strings'
const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 1rem;
  word-wrap: break-word;
  p {
    margin: 0;
  }
`

const EntryCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  padding: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface.background};
`

const ImageContainer = styled.div<{ aspect: number }>`
  position: relative;
  width: 100%;
  padding-bottom: ${({ aspect }) => 100 / aspect}%;

  > div {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const MessageOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  opacity: ${({ $isVisible: isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ $isVisible: isVisible }) =>
    isVisible ? 'visible' : 'hidden'};
  transition:
    opacity 0.2s ease-in-out,
    visibility 0.2s ease-in-out;
  cursor: pointer;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const ViewButton = styled(Button)`
  width: 100%;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.brand.primary};
  color: ${({ theme }) => theme.colors.font.regular};
  border: none;
  cursor: pointer;
  font-weight: 500;
  position: relative;
  z-index: 2;

  &:hover {
    background: ${({ theme }) => theme.colors.brand.primary.active};
    color: ${({ theme }) => theme.colors.font.white};
  }
`

const OverlayMessage = styled(MessageContainer)`
  color: ${({ theme }) => theme.colors.font.white};
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  padding-bottom: 20px;

  p {
    font-size: 1.1rem;
    line-height: 1.5;
    overflow-wrap: break-word;
  }
`

const GuestLabel = styled.p`
  color: ${({ theme }) => theme.colors.font.subdued};
`

const OverlayGuestLabel = styled(GuestLabel)`
  color: rgba(255, 255, 255, 0.8);
  margin-top: auto;
  padding-top: 1rem;
`

interface Props {
  entry: Entry
  aspect?: number
}

export const FeedItem: React.FC<Props> = ({ entry, aspect = 5 / 4 }) => {
  const [showOverlay, setShowOverlay] = useState(false)

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowOverlay(false)
  }

  return entry.images.length > 0 ? (
    <EntryCard>
      <ImageContainer aspect={aspect}>
        {entry.images.map((img) => (
          <Image key={img} imgSrc={img} retryOptions={{ maxAttempts: 3 }} />
        ))}
        <MessageOverlay $isVisible={showOverlay} onClick={handleOverlayClick}>
          <OverlayMessage>
            <p>{entry.message}</p>
            <OverlayGuestLabel>{entry.guestName}</OverlayGuestLabel>
          </OverlayMessage>
        </MessageOverlay>
      </ImageContainer>
      <ViewButton kind="secondary" onClick={() => setShowOverlay(!showOverlay)}>
        {showOverlay ? (
          <FormattedMessage {...strings.hideDetails} />
        ) : (
          <FormattedMessage {...strings.viewDetails} />
        )}
      </ViewButton>
    </EntryCard>
  ) : (
    <ImagelessItem entry={entry} aspect={aspect} />
  )
}

const ImagelessItem: React.FC<Props> = ({ entry, aspect = 5 / 4 }) => {
  return (
    <EntryCard>
      <ImageContainer aspect={aspect}>
        <MessageOverlay $isVisible={true}>
          <OverlayMessage>
            <p>{entry.message}</p>
            <OverlayGuestLabel>{entry.guestName}</OverlayGuestLabel>
          </OverlayMessage>
        </MessageOverlay>
      </ImageContainer>
    </EntryCard>
  )
}
